import { useEffect, useState } from 'react';
import { X, Users, Phone, Voicemail } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';

interface Agent {
  id: string;
  first_name: string;
  last_name: string;
  status: string;
}

interface TransferCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  callSid: string;
  fromAgentId: string;
  onTransfer?: (type: 'agent' | 'phone' | 'voicemail', target: string) => void;
}

type TransferType = 'agent' | 'phone' | 'voicemail';

export function TransferCallModal({
  isOpen,
  onClose,
  callSid,
  fromAgentId,
  onTransfer
}: TransferCallModalProps) {
  const { theme } = useTheme();
  const [transferType, setTransferType] = useState<TransferType>('agent');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || transferType !== 'agent') return;
    let cancelled = false;

    const loadAgents = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('call_center_agents')
          .select('id, first_name, last_name, status')
          .eq('status', 'available');

        if (error) {
          console.error('[TransferCallModal] Failed to load agents:', error);
          return;
        }
        if (!cancelled) {
          // Exclude self
          setAgents((data || []).filter((a) => a.id !== fromAgentId));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAgents();
    return () => {
      cancelled = true;
    };
  }, [isOpen, fromAgentId, transferType]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    let target = '';
    
    // Validate based on transfer type
    if (transferType === 'agent' && !selectedAgentId) return;
    if (transferType === 'phone' && !phoneNumber) return;
    
    if (transferType === 'agent') target = selectedAgentId;
    if (transferType === 'phone') target = phoneNumber;
    if (transferType === 'voicemail') target = 'voicemail';

    setIsSubmitting(true);
    try {
      // Try to log a transfer request so a backend worker can execute the transfer
      try {
        const { error } = await supabase
          .from('call_transfer_requests')
          .insert({
            call_sid: callSid,
            from_agent_id: fromAgentId,
            to_agent_id: transferType === 'agent' ? selectedAgentId : null,
            transfer_type: transferType,
            transfer_target: target,
            requested_at: new Date().toISOString(),
            status: 'requested'
          });

        if (error) {
          console.warn('[TransferCallModal] call_transfer_requests insert failed:', error.message);
        }
      } catch (e) {
        console.warn('[TransferCallModal] call_transfer_requests table not available');
      }

      // Call the onTransfer callback if provided
      if (onTransfer) {
        onTransfer(transferType, target);
      }

      // Close the modal after "request"
      onClose();
    } catch (err) {
      console.error('Error requesting transfer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedAgentId('');
    setPhoneNumber('');
    onClose();
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;
    
    const parts = [];
    if (match[1]) parts.push(match[1]);
    if (match[2]) parts.push(match[2]);
    if (match[3]) parts.push(match[3]);
    
    return parts.join('-');
  };

  const isValid = () => {
    if (transferType === 'agent') return !!selectedAgentId;
    if (transferType === 'phone') return phoneNumber.replace(/\D/g, '').length === 10;
    if (transferType === 'voicemail') return true;
    return false;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{ backgroundColor: theme.colors.bgCard }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" style={{ color: theme.colors.textPrimary }} />
            <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
              Transfer Call
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="transition-colors p-1 rounded"
            style={{ color: theme.colors.textSecondary }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            disabled={isSubmitting}
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Transfer Type Tabs */}
        <div className="flex" style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}>
          <button
            onClick={() => setTransferType('agent')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors flex-1 ${
              transferType === 'agent' ? 'border-b-2 border-blue-500' : ''
            }`}
            style={{
              color: transferType === 'agent' ? theme.colors.infoText : theme.colors.textSecondary,
              backgroundColor: transferType === 'agent' ? theme.colors.bgHover : 'transparent'
            }}
            disabled={isSubmitting}
          >
            <Users className="w-4 h-4" />
            <span>Agent</span>
          </button>

          <button
            onClick={() => setTransferType('phone')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors flex-1 ${
              transferType === 'phone' ? 'border-b-2 border-blue-500' : ''
            }`}
            style={{
              color: transferType === 'phone' ? theme.colors.infoText : theme.colors.textSecondary,
              backgroundColor: transferType === 'phone' ? theme.colors.bgHover : 'transparent'
            }}
            disabled={isSubmitting}
          >
            <Phone className="w-4 h-4" />
            <span>Phone</span>
          </button>

          <button
            onClick={() => setTransferType('voicemail')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors flex-1 ${
              transferType === 'voicemail' ? 'border-b-2 border-blue-500' : ''
            }`}
            style={{
              color: transferType === 'voicemail' ? theme.colors.infoText : theme.colors.textSecondary,
              backgroundColor: transferType === 'voicemail' ? theme.colors.bgHover : 'transparent'
            }}
            disabled={isSubmitting}
          >
            <Voicemail className="w-4 h-4" />
            <span>Voicemail</span>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {transferType === 'agent' && (
            <>
              <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                Select an available agent to transfer this call.
              </p>

              {loading ? (
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Loading available agents...</div>
              ) : agents.length === 0 ? (
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>No available agents to transfer to.</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {agents.map((agent) => {
                    const fullName = `${agent.first_name} ${agent.last_name}`.trim();
                    return (
                      <label
                        key={agent.id}
                        className="flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors"
                        style={{
                          border: `1px solid ${theme.colors.borderColor}`,
                          backgroundColor: selectedAgentId === agent.id ? theme.colors.bgHover : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedAgentId !== agent.id) {
                            e.currentTarget.style.backgroundColor = theme.colors.bgHover;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedAgentId !== agent.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="transfer-agent"
                          value={agent.id}
                          checked={selectedAgentId === agent.id}
                          onChange={(e) => setSelectedAgentId(e.target.value)}
                          className="w-4 h-4"
                          disabled={isSubmitting}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>{fullName || agent.id}</div>
                          <div className="text-xs capitalize" style={{ color: theme.colors.textSecondary }}>{agent.status}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {transferType === 'phone' && (
            <>
              <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                Enter the phone number to transfer this call to.
              </p>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textPrimary }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                  placeholder="555-123-4567"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`
                  }}
                  disabled={isSubmitting}
                  maxLength={12}
                />
                <p className="text-xs mt-1" style={{ color: theme.colors.textTertiary }}>
                  Format: 555-123-4567 (10 digits)
                </p>
              </div>
            </>
          )}

          {transferType === 'voicemail' && (
            <>
              <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                Transfer this call to the main voicemail system.
              </p>
              <div className="p-3 rounded-lg" style={{ backgroundColor: theme.colors.infoBg, border: `1px solid ${theme.colors.borderColor}` }}>
                <p className="text-sm" style={{ color: theme.colors.infoText }}>
                  📬 The caller will be able to leave a message that will be available in your voicemail inbox.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4" style={{ borderTop: `1px solid ${theme.colors.borderColor}`, backgroundColor: theme.colors.bgPage }}>
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{ color: theme.colors.textPrimary }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid() || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: theme.colors.primaryColor }}
            onMouseEnter={(e) => {
              if (isValid() && !isSubmitting) {
                e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primaryColor;
            }}
          >
            {isSubmitting ? 'Transferring...' : 'Transfer Call'}
          </button>
        </div>
      </div>
    </div>
  );
}
